<template>
  <div>
    <!-- Page Header -->
    <SectionHeader
      :show-date-selector="viewConfig.showDateSelector"
      :title="viewConfig.title" />

    <!-- Total Metrics Section (always present) -->
    <TotalMetrics
      :metrics="totalMetrics"
      :row-sizes="totalRowSizes" />

    <v-container class="mt-2" />

    <!-- Detailed Metrics Section -->
    <SectionHeader title="Detaillierte Metriken" />

    <v-row>
      <template
        v-for="(detailConfig, index) in viewConfig.detailedMetrics"
        :key="index">
        <!-- Location Metrics - Each location becomes a card -->
        <template v-if="detailConfig.type === 'location'">
          <v-col
            v-for="location in locationMetricsData[detailConfig.metricGroups ? `exact_${index}` : detailConfig.category!]"
            :key="`${index}-${location.locationName}`"
            :lg="detailConfig.cardWidth?.lg ?? 4"
            :md="detailConfig.cardWidth?.md"
            :sm="detailConfig.cardWidth?.sm ?? 6"
            cols="12">
            <MetricCard
              :location-name="location.locationName"
              :metrics="location.metrics"
              :row-sizes="detailConfig.metricGroups ? getMetricGroupSizes(detailConfig.metricGroups) : undefined"
              :variant="detailConfig.variant" />
          </v-col>
        </template>

        <!-- Comparison Metrics -->
        <v-col
          v-else-if="detailConfig.type === 'comparison' && comparisonMetricsData[detailConfig.metricKey!]"
          cols="12"
          lg="4"
          md="6"
          sm="12">
          <MetricComparison v-bind="comparisonMetricsData[detailConfig.metricKey!]" />
        </v-col>
      </template>
    </v-row>

    <!-- Show school-fee reductions by reason and the total difference per school year -->
    <template v-if="viewName === 'finance'">
      <v-container class="mt-2" />
      <SectionHeader title="Gesamtforderung nach Schuljahr" />
      <TotalDifferenceBreakdown />

      <v-container class="mt-2" />
      <SectionHeader title="Schulgeldreduzierungen" />
      <SchoolFeeReductionsBreakdown />
    </template>

    <!-- Show the pedagogical and non-pedagogical hours by category -->
    <template v-if="viewName === 'staff'">
      <v-container class="mt-2" />
      <SectionHeader title="Stunden nach Kategorie" />
      <v-row>
        <v-col cols="12">
          <HoursByLocationBreakdown group="pedagogical" />
        </v-col>
        <v-col cols="12">
          <HoursByLocationBreakdown group="non_pedagogical" />
        </v-col>
      </v-row>
    </template>

    <!-- Load time indicator -->
    <div
      v-if="metricsStore.loadTime !== 0"
      class="meta">
      Seite geladen in: {{ metricsStore.loadTime }} ms
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { useMetrics } from '@/composables/useMetrics';
import { flattenMetricGroups, getMetricGroupSizes, getViewConfig } from '@/config/viewConfig';
import SectionHeader from '@/layout/section-header/SectionHeader.vue';
import TotalMetrics from '@/components/TotalMetrics.vue';
import MetricComparison from '@/components/metrics/MetricComparison.vue';
import MetricCard from '@/components/metrics/MetricCard.vue';
import SchoolFeeReductionsBreakdown from '@/components/SchoolFeeReductionsBreakdown.vue';
import TotalDifferenceBreakdown from '@/components/TotalDifferenceBreakdown.vue';
import HoursByLocationBreakdown from '@/components/HoursByLocationBreakdown.vue';
import type { LocationMetricsProps, MetricComparisonProps } from '@/models/metricDefinitions';
import { useMetricsStore } from '@/stores/metrics';

interface Props {
  viewName: string;
}

const props = defineProps<Props>();

const { createTotalMetrics, createLocationMetrics, createComparisonMetrics } = useMetrics();
const metricsStore = useMetricsStore();

const viewConfig = getViewConfig(props.viewName);

const metricLoadingStates = {
  totalDifference: metricsStore.totalDifferenceLoadingStates
};

const totalMetrics = createTotalMetrics(
  metricsStore.metrics,
  metricsStore.loadingStates,
  viewConfig.totalMetricsCategory,
  viewConfig.totalMetricGroups ? flattenMetricGroups(viewConfig.totalMetricGroups) : undefined,
  metricLoadingStates
);

const totalRowSizes = viewConfig.totalMetricGroups ? getMetricGroupSizes(viewConfig.totalMetricGroups) : undefined;

const locationMetricsData = computed(() => {
  const result: Record<string, LocationMetricsProps['locationData']> = {};

  viewConfig.detailedMetrics.forEach((detailConfig, index) => {
    if (detailConfig.type === 'location') {
      const key = detailConfig.metricGroups ? `exact_${index}` : detailConfig.category!;

      if (detailConfig.metricGroups) {
        // Use specified metrics
        result[key] = createLocationMetrics(
          metricsStore.metrics,
          metricsStore.loadingStates,
          viewConfig.totalMetricsCategory,
          flattenMetricGroups(detailConfig.metricGroups),
          metricLoadingStates
        ).value;
      } else if (detailConfig.category) {
        // Use category-based metrics
        result[key] = createLocationMetrics(
          metricsStore.metrics,
          metricsStore.loadingStates,
          detailConfig.category,
          undefined,
          metricLoadingStates
        ).value;
      }
    }
  });

  return result;
});

const comparisonMetricsData = computed(() => {
  const result: Record<string, MetricComparisonProps> = {};

  viewConfig.detailedMetrics.forEach((detailConfig) => {
    if (detailConfig.type === 'comparison' && detailConfig.metricKey) {
      result[detailConfig.metricKey] = createComparisonMetrics(
        metricsStore.metrics,
        metricsStore.loadingStates,
        detailConfig.metricKey,
        metricLoadingStates,
        detailConfig.subMetric
      ).value;
    }
  });

  return result;
});

onMounted((): void => {
  metricsStore.initializeLoadingStates();
  metricsStore.fetchAllMetrics();
});
</script>

<style scoped>
.meta {
  text-align: end;
  margin-top: 20px;
  font-size: 0.8rem;
}
</style>
